import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { GoogleMap, useJsApiLoader, Marker, Polyline, InfoWindow } from "@react-google-maps/api";
import { API , authFetch } from "@/App";
import { MapPin, Navigation, Battery, Clock, RefreshCw, AlertTriangle, ChevronDown, User } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const mapContainerStyle = { width: "100%", height: "600px", borderRadius: "12px" };
const defaultCenter = { lat: 23.0, lng: 72.6 };
const ROLE_COLORS = { AREA_MANAGER: "#8B1A1A", SALES_EXECUTIVE: "#3B82F6", PROMOTOR: "#10B981" };

const LiveTrackingPage = () => {
  const [searchParams] = useSearchParams();
  const [positions, setPositions] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [trail, setTrail] = useState(null);
  const [empSummary, setEmpSummary] = useState(null);
  const [mapError, setMapError] = useState(false);
  const [expandedPanel, setExpandedPanel] = useState(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || "",
  });

  useEffect(() => {
    window.gm_authFailure = () => setMapError(true);
    return () => { window.gm_authFailure = undefined; };
  }, []);

  const fetchPositions = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/gps/live`, {  });
      const data = await res.json();
      if (data.success) setPositions(data.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchEmpSummary = useCallback(async (empId) => {
    try {
      const res = await authFetch(`${API}/field-force/employee-summary/${empId}`, {  });
      const data = await res.json();
      if (data.success) {
        setEmpSummary(data.data);
        setTrail(data.data.gps_trail || []);
      }
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    fetchPositions();
    const interval = setInterval(fetchPositions, 30000);
    return () => clearInterval(interval);
  }, [fetchPositions]);

  useEffect(() => {
    const empId = searchParams.get("employee");
    if (empId && positions.length > 0) {
      const emp = positions.find(p => p.employee_id === empId);
      if (emp) { setSelectedEmp(emp); fetchEmpSummary(empId); }
    }
  }, [searchParams, positions, fetchEmpSummary]);

  const handleSelectEmployee = (emp) => {
    setSelectedEmp(emp);
    setExpandedPanel(emp.employee_id);
    fetchEmpSummary(emp.employee_id);
  };

  const mapCenter = positions.length > 0 ? { lat: positions[0].lat, lng: positions[0].lng } : defaultCenter;
  const showMap = isLoaded && !loadError && !mapError;

  return (
    <div className="space-y-4" data-testid="live-tracking-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Live Tracking</h1>
          <p className="text-slate-500 text-sm mt-1">Real-time employee GPS positions and routes</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              await authFetch(`${API}/gps/simulate`, { method: "POST",  });
              toast.success("GPS data simulated");
              fetchPositions();
            }}
            className="px-3 py-1.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
            data-testid="simulate-gps-btn"
          >
            Simulate GPS
          </button>
          <button onClick={fetchPositions} className="px-3 py-1.5 text-xs font-medium bg-[#8B1A1A] text-white rounded-lg hover:bg-[#6B1515]" data-testid="refresh-btn">
            <RefreshCw className="w-3 h-3 inline mr-1" /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Panel: Employee List */}
        <div className="space-y-2 max-h-[640px] overflow-y-auto pr-1" data-testid="employee-list-panel">
          {positions.map((emp) => (
            <Card
              key={emp.employee_id}
              className={`p-3 cursor-pointer transition-all hover:shadow-md ${selectedEmp?.employee_id === emp.employee_id ? "ring-2 ring-[#8B1A1A] bg-red-50/30" : ""}`}
              onClick={() => handleSelectEmployee(emp)}
              data-testid={`emp-card-${emp.employee_id}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold" style={{ backgroundColor: ROLE_COLORS[emp.role] || "#6B7280" }}>
                  {emp.full_name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{emp.full_name}</p>
                  <p className="text-xs text-slate-500">{emp.employee_code} &middot; {emp.role?.replace(/_/g, " ")}</p>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {emp.is_stoppage ? (
                    <Badge variant="outline" className="text-yellow-700 border-yellow-300 bg-yellow-50 text-xs">Stop</Badge>
                  ) : (
                    <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50 text-xs">Moving</Badge>
                  )}
                  {emp.battery_level != null && (
                    <span className={`text-xs font-medium flex items-center gap-0.5 ${emp.battery_level > 20 ? "text-emerald-600" : "text-red-600"}`}>
                      <Battery className="w-3 h-3" />{emp.battery_level}%
                    </span>
                  )}
                </div>
              </div>

              {/* Expanded Summary */}
              {expandedPanel === emp.employee_id && empSummary && (
                <div className="mt-3 pt-3 border-t border-slate-100 space-y-2 text-xs text-slate-600">
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-slate-400">Check In:</span> <span className="font-medium">{empSummary.attendance?.check_in_time || "—"}</span></div>
                    <div><span className="text-slate-400">Check Out:</span> <span className="font-medium">{empSummary.attendance?.check_out_time || "—"}</span></div>
                    <div><span className="text-slate-400">Distance:</span> <span className="font-medium">{empSummary.total_distance_km} km</span></div>
                    <div><span className="text-slate-400">Stoppages:</span> <span className="font-medium">{empSummary.stoppage_count}</span></div>
                    <div><span className="text-slate-400">GPS Pings:</span> <span className="font-medium">{empSummary.total_pings}</span></div>
                    <div><span className="text-slate-400">Hours:</span> <span className="font-medium">{empSummary.attendance?.hours_on_field || 0}h</span></div>
                  </div>
                  {empSummary.stoppages?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-slate-400 mb-1">Stoppage Locations:</p>
                      {empSummary.stoppages.slice(0, 3).map((s, i) => (
                        <p key={i} className="text-slate-600 pl-2 border-l-2 border-yellow-300 mb-1">
                          {s.lat.toFixed(4)}, {s.lng.toFixed(4)} &middot; {s.duration_minutes} min
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
          {positions.length === 0 && (
            <div className="text-center py-8 text-slate-500 text-sm">
              <MapPin className="w-6 h-6 mx-auto mb-2 text-slate-300" />
              No active positions. Click "Simulate GPS" to generate demo data.
            </div>
          )}
        </div>

        {/* Right Panel: Map */}
        <div className="lg:col-span-2">
          {(loadError || mapError) && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                Add <code className="bg-amber-100 px-1 rounded">*.preview.emergentagent.com/*</code> to Google Maps API key HTTP referrer restrictions.
              </p>
            </div>
          )}
          {showMap ? (
            <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
              <GoogleMap mapContainerStyle={mapContainerStyle} center={selectedEmp ? { lat: selectedEmp.lat, lng: selectedEmp.lng } : mapCenter} zoom={selectedEmp ? 12 : 9}
                options={{ styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }], mapTypeControl: true, streetViewControl: false, fullscreenControl: true }}>
                {positions.map((emp) => (
                  <Marker key={emp.employee_id} position={{ lat: emp.lat, lng: emp.lng }} onClick={() => handleSelectEmployee(emp)}
                    icon={{ path: window.google?.maps?.SymbolPath?.CIRCLE || 0, scale: selectedEmp?.employee_id === emp.employee_id ? 14 : 10, fillColor: ROLE_COLORS[emp.role] || "#6B7280", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 }}
                    title={emp.full_name} />
                ))}
                {trail && trail.length > 1 && (
                  <Polyline path={trail.map(p => ({ lat: p.lat, lng: p.lng }))} options={{ strokeColor: "#8B1A1A", strokeOpacity: 0.8, strokeWeight: 3 }} />
                )}
                {empSummary?.stoppages?.map((s, i) => (
                  <Marker key={`stop-${i}`} position={{ lat: s.lat, lng: s.lng }}
                    icon={{ path: window.google?.maps?.SymbolPath?.BACKWARD_CLOSED_ARROW || 3, scale: 6, fillColor: "#EAB308", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 }}
                    title={`Stoppage: ${s.duration_minutes} min`} />
                ))}
                {selectedEmp && (
                  <InfoWindow position={{ lat: selectedEmp.lat, lng: selectedEmp.lng }} onCloseClick={() => setSelectedEmp(null)}>
                    <div className="p-1 min-w-[180px]">
                      <p className="font-semibold text-sm">{selectedEmp.full_name}</p>
                      <p className="text-xs text-slate-500">{selectedEmp.employee_code}</p>
                      <div className="mt-1 text-xs space-y-0.5">
                        <p><Navigation className="w-3 h-3 inline" /> {selectedEmp.speed || 0} km/h</p>
                        {selectedEmp.battery_level && <p><Battery className="w-3 h-3 inline" /> {selectedEmp.battery_level}%</p>}
                      </div>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            </div>
          ) : (
            <Card className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {positions.map((emp) => (
                  <div key={emp.employee_id} className="p-3 border rounded-lg">
                    <p className="font-medium text-sm">{emp.full_name}</p>
                    <p className="text-xs text-slate-500 mt-1">{emp.lat?.toFixed(4)}, {emp.lng?.toFixed(4)} &middot; {emp.speed || 0} km/h</p>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveTrackingPage;
